import React from "react";
import { Dialog } from "@mui/material";
import { X } from "@phosphor-icons/react";
import type { DuplicateUploadInfo } from "./FileTree/UploadStuff/useFileTrayStore";
import { openDocumentInViewer } from "../FpPages/RMS/Dashboard/Cards/openInViewer";

interface DuplicateUploadModalProps {
  open: boolean;
  onClose: () => void;
  duplicates: DuplicateUploadInfo[];
}

const DuplicateUploadModal: React.FC<DuplicateUploadModalProps> = ({
  open,
  onClose,
  duplicates,
}) => {
  const handleOpenDocument = (dup: DuplicateUploadInfo) => {
    openDocumentInViewer(dup.existingBlobId, dup.existingName);
    onClose();
  };

  const title =
    duplicates.length === 1
      ? "Duplicate File Detected"
      : `${duplicates.length} Duplicate Files Detected`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          width: "480px",
          maxWidth: "90vw",
        },
      }}
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="text-sm text-gray-600 mb-4">
          {duplicates.length === 1
            ? "This file already exists in your organization."
            : "These files already exist in your organization."}
        </div>

        <div className="max-h-[300px] overflow-auto">
          {duplicates.map((dup, i) => {
            const folderDisplay =
              dup.existingPath.length > 0
                ? dup.existingPath.join(" / ")
                : "Root";

            return (
              <div
                key={`${dup.existingBlobId}-${i}`}
                className="flex items-start justify-between py-3 border-b border-gray-100 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-gray-800 truncate">
                    {dup.uploadedName}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Matches{" "}
                    <button
                      onClick={() => handleOpenDocument(dup)}
                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                    >
                      {dup.existingName}
                    </button>{" "}
                    in {folderDisplay}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors duration-200"
          >
            Dismiss
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default DuplicateUploadModal;
